import { CHALLENGED_INCIDENT_TYPE } from 'containers/ResolutionCenter/constants'
import {
  ALL_TIMES,
  LAST_3_DAYS,
  LAST_7_DAYS,
  LAST_24_HOURS,
  LAST_30_DAYS,
} from 'containers/ResolutionCenter/IncidentFilters/constants'

import { FIELD_BY_ID, resolveFieldForState } from '../incidentFilterCatalog'

const TIME_TO_DATE = {
  '24h': LAST_24_HOURS,
  '3d': LAST_3_DAYS,
  '7d': LAST_7_DAYS,
  '30d': LAST_30_DAYS,
  all: ALL_TIMES,
}
const DATE_TO_TIME = Object.fromEntries(Object.entries(TIME_TO_DATE).map(([k, v]) => [v, k]))

// Challenge type mapping: redesign strings ↔ server numbers
const CHALLENGE_TYPE_TO_SERVER = {
  'release-request': CHALLENGED_INCIDENT_TYPE.RELEASE_REQUEST, // 1
  'encrypted-attachment': CHALLENGED_INCIDENT_TYPE.ENCRYPTED_ATTACHMENT, // 2
  'end-user-report': CHALLENGED_INCIDENT_TYPE.SAFE_CHALLENGED, // 3
}
const SERVER_TO_CHALLENGE_TYPE = Object.fromEntries(
  Object.entries(CHALLENGE_TYPE_TO_SERVER).map(([k, v]) => [v, k])
)

// Object.hasOwn (not `in`) — `in` also matches inherited/prototype keys, so a deep link like
// ?dateFilter=constructor or ?dateFilter=toString would otherwise resolve to a real, non-null
// value (e.g. the Object constructor itself) instead of correctly falling through to null.
export const timeFrameToDateFilter = (tf) =>
  Object.hasOwn(TIME_TO_DATE, tf) ? TIME_TO_DATE[tf] : null
export const dateFilterToTimeFrame = (df) =>
  Object.hasOwn(DATE_TO_TIME, df) ? DATE_TO_TIME[df] : null

/**
 * Starring a preset must not change the active time frame.
 * When the list is still following the favorite (`dateFilter` unset), pin the
 * currently displayed preset into the store so a new favorite cannot retarget
 * `dateFilter || effectiveDateFilter`.
 *
 * @returns {number|null} dateFilter to dispatch without submit, or null if already pinned
 */
export const dateFilterToPinWhenFavoriteChanges = (dateFilter, currentTimeFrame) => {
  if (dateFilter != null) return null
  if (!currentTimeFrame || currentTimeFrame === 'custom') return null
  return timeFrameToDateFilter(currentTimeFrame)
}

// Object.hasOwn here too — see the note above timeFrameToDateFilter/dateFilterToTimeFrame.
export const challengeTypeToServer = (ct) =>
  Object.hasOwn(CHALLENGE_TYPE_TO_SERVER, ct) ? CHALLENGE_TYPE_TO_SERVER[ct] : null
export const serverToChallengeType = (sc) =>
  Object.hasOwn(SERVER_TO_CHALLENGE_TYPE, sc) ? SERVER_TO_CHALLENGE_TYPE[sc] : null

// Same chip id (`verdict`) is "Themis verdict" on Unclassified and "Classification" on
// Classified/Challenged — different option sets / meanings. Drop it when crossing that boundary
// so persisted chips don't keep invalid values (e.g. no_threat on Classified, Safe on Unclassified).
// Classified-only chips (e.g. Resolved) are dropped when landing on Unclassified.
// Unclassified-only chips (Bubble Ups / Auto Themis) are dropped when leaving Unclassified.
// Incident type options differ on Challenged (IRONSCALES/MSFT) vs other tabs (Email/ATO/Teams).
const isUnclassifiedTab = (urlState) => !urlState || urlState === 'unclassified'
const isChallengedTab = (urlState) => urlState === 'challenged'

export const pruneFiltersForTabChange = (chips, prevUrlState, nextUrlState) => {
  const list = chips || []
  if (prevUrlState == null || nextUrlState == null) return list
  const crossedVerdictBoundary = isUnclassifiedTab(prevUrlState) !== isUnclassifiedTab(nextUrlState)
  const crossedTypeBoundary = isChallengedTab(prevUrlState) !== isChallengedTab(nextUrlState)
  if (!crossedVerdictBoundary && !crossedTypeBoundary) return list

  const nextUnclassified = isUnclassifiedTab(nextUrlState)
  const pruned = list.filter((c) => {
    if (crossedVerdictBoundary && c.fieldId === 'verdict') return false
    if (crossedVerdictBoundary && nextUnclassified && FIELD_BY_ID[c.fieldId]?.classifiedOnly) {
      return false
    }
    if (crossedVerdictBoundary && !nextUnclassified && FIELD_BY_ID[c.fieldId]?.unclassifiedOnly) {
      return false
    }
    if (crossedTypeBoundary && c.fieldId === 'type' && !c.locked) return false
    return true
  })
  return pruned.length === list.length ? list : pruned
}

// Classification quickFilters (unified `state`) only apply on classified/challenged.
// Unclassified "Themis verdict" chips use themisVerdictFilter instead — suggested
// verdict, not applied classification state.
const CLASSIFICATION_QUICK_URL_STATES = new Set(['classified', 'challenged'])

// Backend report_consts verdict codes (PHISHING=1, SPAM=2, SAFE=4).
const THEMIS_VERDICT_VALUE_MAP = {
  Phishing: 1,
  Spam: 2,
  no_threat: 4,
}

// Convert active redesign chips to server filter payloads.
// opts.urlState: current tab — gates Classification → quickFilters (see above).
export const buildServerFilterUpdates = (active, opts = {}) => {
  const { urlState } = opts
  const regularFilters = {
    detectedByFilter: [],
    includesFilter: [],
    excludesFilter: [],
    assignedToFilter: [],
    resolvedFilter: [],
    themisVerdictFilter: [],
    challengeSourceFilter: [],
  }
  const quickFilters = []

  active.forEach((chip) => {
    // Challenged Incident type (and Classification label) resolve via tab — use the
    // tab-aware field so `type` maps to challengeSourceFilter instead of quickFilters.
    const field = resolveFieldForState(FIELD_BY_ID[chip.fieldId], urlState)
    if (!field || !field.server) return

    const { server } = field

    switch (server.kind) {
      case 'detectedByFilter':
      case 'resolvedFilter':
      case 'challengeSourceFilter':
        chip.values.forEach((value) => {
          const serverValue = server.valueMap[value]
          if (serverValue != null) {
            regularFilters[server.kind].push(serverValue)
          }
        })
        break

      case 'quickFilters':
        if (field.id === 'verdict' && !CLASSIFICATION_QUICK_URL_STATES.has(urlState)) {
          // Unclassified "Themis verdict" → themisVerdictFilter (suggested verdict).
          chip.values.forEach((value) => {
            const serverValue = THEMIS_VERDICT_VALUE_MAP[value]
            if (serverValue != null && !regularFilters.themisVerdictFilter.includes(serverValue)) {
              regularFilters.themisVerdictFilter.push(serverValue)
            }
          })
          break
        }
        chip.values.forEach((value) => {
          const serverValue = server.valueMap[value]
          if (serverValue == null) return
          // Allow one chip value → multiple quick-filter ids (e.g. Phishing → phishing + compromised).
          if (Array.isArray(serverValue)) {
            serverValue.forEach((v) => {
              if (v != null && !quickFilters.includes(v)) quickFilters.push(v)
            })
          } else if (!quickFilters.includes(serverValue)) {
            quickFilters.push(serverValue)
          }
        })
        break

      case 'includeExclude':
        if (chip.values.length > 0) {
          const value = chip.values[0]
          if (value === 'Include') {
            regularFilters.includesFilter.push(server.value)
          } else if (value === 'Exclude') {
            regularFilters.excludesFilter.push(server.value)
          }
        }
        break

      case 'assigneeFilter':
        // Legacy IncidentFilters stores assignee objects (`{ id, … }`); List epic sends
        // `assignedToFilter.map((e) => e.id)`. Chips only hold option values (raw ids) — wrap
        // them so assigneeFilter is never [null, …] / nullified in the request body.
        chip.values.forEach((value) => {
          if (value == null || value === '') return
          const id = typeof value === 'object' ? (value.id ?? value.value) : value
          if (id == null || id === '') return
          regularFilters.assignedToFilter.push({ id })
        })
        break

      case 'unsupported':
      case 'deferred':
        // Ignored - contributes nothing
        break

      default:
        // Unknown server kind - ignore
        break
    }
  })

  return { regularFilters, quickFilters }
}

// Order-insensitive fingerprint of a filter payload, used by applyChipFilters to tell whether a chip
// edit actually changes what the backend is asked for: adding a chip (or removing one that was never
// filled) leaves every array untouched, so the list must not refetch. Values are stringified
// individually, so both raw ids and {id,...} assignee objects compare correctly.
export const serializeServerFilterPayload = (regularFilters = {}, quickFilters = []) => {
  const norm = (arr) => (arr || []).map((v) => JSON.stringify(v)).sort()
  const entries = Object.keys(regularFilters)
    .sort()
    .map((key) => [key, norm(regularFilters[key])])
  return JSON.stringify([...entries, ['quickFilters', norm(quickFilters)]])
}
